import {
  Box,
  CommonPopupCrud,
  customUseEffect,
  Grid,
  MuiAutocomplete,
  MyTextInput,
  timeoutCall,
} from "@common";
import {
  getActivityApi,
  getActivityNameById,
  getActivityType,
  getCrmPriority,
  getProjectUser,
} from "@features";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useFormik } from "formik";
import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

dayjs.extend(customParseFormat);

const validationSchema = Yup.object({
  activityType: Yup.object()
    .nullable()
    .required("Activity Type is required"),
  activityName: Yup.object()
    .nullable()
    .required("Activity Name is required"),
  dueDate: Yup.mixed().required("Due Date is required"),
  actualDate: Yup.mixed().nullable(),
  priority: Yup.mixed().nullable().required("Priority is required"),
  description: Yup.string()
    .max(500, "Description should contain below 500 characters"),
  assignedTo: Yup.object().nullable(),
  outComeNote: Yup.string().nullable()
    .max(500, "Outcome Note should contain below 500 characters"),
});

const sanitizeId = (id) => {
  if (id === true || id === false || id === "true" || id === "false" || id === undefined || id === null) {
    return null;
  }
  return id;
};

const getSelectedId = (data, fallbackId) => {
  const cleanFallback = sanitizeId(fallbackId);
  if (Array.isArray(data)) {
    return sanitizeId(data[0]?.id || data[0]?._id) || cleanFallback || null;
  }
  return sanitizeId(data?.id || data?._id) || cleanFallback || null;
};

const parseDateTimeValue = (val) => {
  if (!val) return null;
  if (dayjs.isDayjs(val)) return val.isValid() ? val : null;
  const parsed = dayjs(val);
  return parsed.isValid() ? parsed : null;
};

const formatDateTimeValue = (val) => {
  if (!val) return null;
  if (dayjs.isDayjs(val)) {
    return val.isValid() ? val.toISOString() : null;
  }
  const parsed = dayjs(val);
  return parsed.isValid() ? parsed.toISOString() : null;
};

const resolveActivityTypeId = (selectedType, options = []) => {
  if (typeof selectedType === "object" && selectedType !== null) {
    return selectedType?.id || selectedType?._id || selectedType?.activityTypeId || null;
  }
  if (typeof selectedType === "string") {
    const found = options.find(
      (opt) => opt?.activityType === selectedType || opt?.name === selectedType || opt?.id === selectedType
    );
    return found?.id || null;
  }
  return null;
};

const AddPage = ({
  showAdd,
  close,
  getFunction,
  forCampaign,
  forProspect,
  forInquiry,
  forLead,
  forOpportunity,
  forAccount,
  forContact,
  forContract,
  forOrder,
  forCustomer,
  customerId: propCustomerId,
  ...props
}) => {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.activity?.loading);
  const activityTypeOptions = useSelector((state) => state?.activityType?.data) || [];
  const activityNameOptions = useSelector((state) => state?.activityName?.data) || [];
  const projectUsersOptions = useSelector((state) => state?.projectUsers?.data) || [];
  const crmPriorityState = useSelector((state) => state?.crmPriority);
  const priorityOptions = crmPriorityState?.data || [];

  // Entity Selected Data resolution from Redux state
  const campaignSelectedData = useSelector((state) => state?.campaign?.selectedData);
  const prospectSelectedData = useSelector((state) => state?.prospect?.selectedData);
  const inquirySelectedData = useSelector((state) => state?.inquiry?.selectedData);
  const leadSelectedData = useSelector((state) => state?.lead?.selectedData);
  const opportunitySelectedData = useSelector((state) => state?.opportunity?.selectedData);
  const accountSelectedData = useSelector((state) => state?.account?.selectedData);
  const contactSelectedData = useSelector((state) => state?.contact?.selectedData);
  const contractSelectedData = useSelector((state) => state?.contract?.selectedData);
  const orderSelectedData = useSelector((state) => state?.order?.selectedData);
  const customerSelectedData = useSelector((state) => state?.customer?.selectedData);

  const propsWithFlags = {
    forCampaign,
    forProspect,
    forInquiry,
    forLead,
    forOpportunity,
    forAccount,
    forContact,
    forContract,
    forOrder,
    forCustomer,
    customerId: propCustomerId,
    ...props,
  };

  const api = useMemo(
    () => getActivityApi(propsWithFlags),
    [
      forCampaign,
      forProspect,
      forInquiry,
      forLead,
      forOpportunity,
      forAccount,
      forContact,
      forContract,
      forOrder,
      forCustomer,
      propCustomerId,
      props?.campaignId,
      props?.prospectId,
      props?.inquiryId,
      props?.leadId,
      props?.opportunityId,
      props?.customerAccountId,
      props?.contactId,
      props?.contractId,
      props?.orderId,
      props?.customerId,
      props?.entityType,
    ]
  );

  const activeEntityId = useMemo(() => {
    const rawPropId = sanitizeId(props?.[api.idKey]);
    if (rawPropId && (typeof rawPropId === "string" || typeof rawPropId === "number")) {
      return rawPropId;
    }
    let resolved = null;
    switch (api.entityType) {
      case "contract":
        resolved = sanitizeId(props?.contractId) || getSelectedId(contractSelectedData);
        break;
      case "customer-account":
        resolved = sanitizeId(props?.customerAccountId) || getSelectedId(accountSelectedData);
        break;
      case "contact":
        resolved = sanitizeId(props?.contactId) || getSelectedId(contactSelectedData);
        break;
      case "opportunity":
        resolved = sanitizeId(props?.opportunityId) || getSelectedId(opportunitySelectedData);
        break;
      case "lead":
        resolved = sanitizeId(props?.leadId) || getSelectedId(leadSelectedData);
        break;
      case "campaign":
        resolved = sanitizeId(props?.campaignId) || getSelectedId(campaignSelectedData);
        break;
      case "inquiry":
        resolved = sanitizeId(props?.inquiryId) || getSelectedId(inquirySelectedData);
        break;
      case "prospect":
        resolved = sanitizeId(props?.prospectId) || getSelectedId(prospectSelectedData);
        break;
      case "customer":
        resolved = sanitizeId(propCustomerId) || sanitizeId(props?.customerId) || getSelectedId(customerSelectedData);
        break;
      case "order":
        resolved = sanitizeId(props?.orderId) || getSelectedId(orderSelectedData);
        break;
      default:
        resolved = null;
    }
    return sanitizeId(resolved);
  }, [
    api.entityType,
    api.idKey,
    props,
    propCustomerId,
    contractSelectedData,
    accountSelectedData,
    contactSelectedData,
    opportunitySelectedData,
    leadSelectedData,
    campaignSelectedData,
    inquirySelectedData,
    prospectSelectedData,
    customerSelectedData,
    orderSelectedData,
  ]);

  customUseEffect(() => {
    dispatch(getActivityType());
    dispatch(getProjectUser());
    dispatch(getCrmPriority());
  }, [dispatch]);

  const formik = useFormik({
    initialValues: {
      activityType: null,
      activityName: null,
      dueDate: null,
      actualDate: null,
      priority: null,
      description: "",
      assignedTo: null,
      outComeNote: "",
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const cleanEntityId = sanitizeId(activeEntityId);
      const payload = {
        ...(cleanEntityId ? { [api.idKey]: cleanEntityId } : {}),
        activityType: values?.activityType?.activityType || values?.activityType?.name || values?.activityType || "",
        activityName: values?.activityName?.activityName || values?.activityName?.name || values?.activityName || "",
        dueDate: formatDateTimeValue(values?.dueDate),
        actualDate: formatDateTimeValue(values?.actualDate),
        priority:
          typeof values?.priority === "object"
            ? values?.priority?.priorityType || values?.priority?.name || null
            : values?.priority || null,
        description: values?.description || "",
        assignedToId: values?.assignedTo?.id || values?.assignedTo?.userId || values?.assignedTo || null,
        outComeNote: values?.outComeNote || "",
      };

      const result = await dispatch(api.add(payload)).then((res) => {
        const isSuccess = Boolean(
          res?.payload?.ok ||
          res?.payload?.data?.ok ||
          res?.meta?.requestStatus === "fulfilled" ||
          !res?.error
        );
        if (isSuccess) {
          getFunction?.();
          timeoutCall(() => {
            formik.resetForm();
          });
          return true;
        }
        return false;
      });
      return result;
    },
  });

  const selectedActivityType = formik?.values?.activityType;

  customUseEffect(() => {
    const typeId = resolveActivityTypeId(selectedActivityType, activityTypeOptions);
    if (typeId) {
      dispatch(getActivityNameById({ activityTypeId: typeId }));
    }
  }, [selectedActivityType, activityTypeOptions, dispatch]);

  const handleClose = useCallback(() => {
    close?.(false);
  }, [close]);

  const handleSubmit = useCallback(() => {
    return formik.submitForm();
  }, [formik]);

  return (
    <CommonPopupCrud
      open={showAdd}
      heading={"Add Activity"}
      close={handleClose}
      handleSubmit={handleSubmit}
      loading={loading}
    >
      <Box component="form" onSubmit={formik.handleSubmit} noValidate>
        <Grid container spacing={2}>
          <Grid xs={12} sm={6}>
            <MuiAutocomplete
              name="activityType"
              label="Activity Type"
              required={true}
              fullWidth={true}
              margin="normal"
              options={activityTypeOptions}
              getOptionLabel={(option) => typeof option === "string" ? option : (option?.activityType || option?.name || "")}
              value={formik.values.activityType}
              onChange={(e, val) => {
                formik.setFieldValue("activityType", val);
                formik.setFieldValue("activityName", null);
                const typeId = val?.id || val?._id || val?.activityTypeId;
                if (typeId) {
                  dispatch(getActivityNameById({ activityTypeId: typeId }));
                }
              }}
              error={formik.touched.activityType && Boolean(formik.errors.activityType)}
              helperText={formik.touched.activityType && formik.errors.activityType}
            />
          </Grid>
          <Grid xs={12} sm={6}>
            <MuiAutocomplete
              name="activityName"
              label="Activity Name"
              required={true}
              fullWidth={true}
              margin="normal"
              options={activityNameOptions}
              disabled={!formik.values.activityType}
              getOptionLabel={(option) => typeof option === "string" ? option : (option?.activityName || option?.name || "")}
              value={formik.values.activityName}
              onChange={(e, val) => formik.setFieldValue("activityName", val)}
              error={formik.touched.activityName && Boolean(formik.errors.activityName)}
              helperText={formik.touched.activityName && formik.errors.activityName}
            />
          </Grid>
        </Grid>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={1} sx={{ mt: 0.5 }}>
            <Grid xs={12} sm={6}>
              <DateTimePicker
                label="Due Date *"
                format="DD/MM/YYYY hh:mm A"
                value={parseDateTimeValue(formik.values.dueDate)}
                onChange={(val) => {
                  formik.setFieldValue("dueDate", val);
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: "normal",
                    onBlur: () => formik.setFieldTouched("dueDate", true),
                    error: formik.touched.dueDate && Boolean(formik.errors.dueDate),
                    helperText: formik.touched.dueDate && formik.errors.dueDate,
                  },
                }}
              />
            </Grid>
            <Grid xs={12} sm={6}>
              <DateTimePicker
                label="Actual Date"
                format="DD/MM/YYYY hh:mm A"
                value={parseDateTimeValue(formik.values.actualDate)}
                onChange={(val) => {
                  formik.setFieldValue("actualDate", val);
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: "normal",
                    onBlur: () => formik.setFieldTouched("actualDate", true),
                    error: formik.touched.actualDate && Boolean(formik.errors.actualDate),
                    helperText: formik.touched.actualDate && formik.errors.actualDate,
                  },
                }}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>

        <Grid container spacing={1} sx={{ mt: 0.5 }}>
          <Grid xs={12} sm={6}>
            <MuiAutocomplete
              name="priority"
              label="Priority"
              required={true}
              fullWidth={true}
              margin="normal"
              options={priorityOptions}
              getOptionLabel={(option) =>
                typeof option === "string"
                  ? option
                  : option?.priorityType || option?.name || ""
              }
              value={formik.values.priority}
              onChange={(e, val) => formik.setFieldValue("priority", val)}
              error={formik.touched.priority && Boolean(formik.errors.priority)}
              helperText={formik.touched.priority && formik.errors.priority}
            />
          </Grid>
          <Grid xs={12} sm={6}>
            <MuiAutocomplete
              name="assignedTo"
              label="Assigned To"
              required={false}
              fullWidth={true}
              margin="normal"
              options={projectUsersOptions}
              getOptionLabel={(option) => option?.userName || option?.email || option?.firstName || ""}
              value={formik.values.assignedTo}
              onChange={(e, val) => formik.setFieldValue("assignedTo", val)}
              error={formik.touched.assignedTo && Boolean(formik.errors.assignedTo)}
              helperText={formik.touched.assignedTo && formik.errors.assignedTo}
            />
          </Grid>
        </Grid>

        <Grid container spacing={1} sx={{ mt: 2 }}>
          <Grid xs={12} sm={12}>
            <MyTextInput
              name="description"
              label="Description"
              multiline
              rows={3}
              fullWidth
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.description && Boolean(formik.errors.description)}
              helperText={formik.touched.description && formik.errors.description}
            />
          </Grid>
          <Grid xs={12} sm={12}>
            <MyTextInput
              name="outComeNote"
              label="Outcome Note"
              multiline
              rows={3}
              fullWidth
              value={formik.values.outComeNote}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.outComeNote && Boolean(formik.errors.outComeNote)}
              helperText={formik.touched.outComeNote && formik.errors.outComeNote}
            />
          </Grid>
        </Grid>
      </Box>
    </CommonPopupCrud>
  );
};

export default AddPage;
